import * as ENGINE from '@gnsx/genesys.js';

import { findAssetReferences } from './assetDependencyAnalyzer';
import { rewriteGlbJsonPaths } from './gltfPathReferences';

import type { AssetType } from './assetTypes';

import { assetsStore, editorStore } from '@/editor/store';

/** Describes a single path update within a file */
export interface PathUpdate {
  jsonPath: string;      // e.g., "actors[0].prefabName"
  oldValue: string;
  newValue: string;
}

/** Describes all updates needed for a single file */
export interface UpdateOperation {
  filePath: string;
  fileType: AssetType;
  updates: PathUpdate[];
}

/** Complete plan for renaming an asset and updating all references */
export interface PathUpdatePlan {
  oldPath: string;
  newPath: string;
  updateOperations: UpdateOperation[];
  affectedAssetCount: number;
}

/** Backup of a file for rollback purposes */
export interface FileBackup {
  path: string;
  content: Blob | string | ArrayBuffer;
}

const DEBUG = {
  logPlanCreation: true,
  logUpdates: true,
  logBackups: false,
} as const;

/** Create a complete update plan for renaming an asset */
export async function createUpdatePlan(oldPath: string, newPath: string): Promise<PathUpdatePlan> {
  if (DEBUG.logPlanCreation) {
    console.log(`📋 Creating update plan: ${oldPath} → ${newPath}`);
  }

  // Find all references to the old path
  const references = await findAssetReferences(oldPath);

  // Group references by source file
  const fileUpdates = new Map<string, PathUpdate[]>();

  for (const ref of references) {
    const updates = fileUpdates.get(ref.sourceAssetPath) ?? [];
    updates.push({
      jsonPath: ref.location,
      oldValue: ref.referencePath,
      newValue: newPath,
    });
    fileUpdates.set(ref.sourceAssetPath, updates);
  }

  // Convert to update operations
  const updateOperations: UpdateOperation[] = [];
  for (const [filePath, updates] of fileUpdates) {
    const asset = assetsStore.assetTree.files.get(filePath);
    if (asset) {
      updateOperations.push({
        filePath,
        fileType: asset.assetType,
        updates,
      });
    }
  }

  const plan: PathUpdatePlan = {
    oldPath,
    newPath,
    updateOperations,
    affectedAssetCount: updateOperations.length,
  };

  if (DEBUG.logPlanCreation) {
    console.log(`📋 Plan created: ${plan.affectedAssetCount} files to update.`);
  }

  return plan;
}

/** Update a single file with path replacements */
async function updateFile(operation: UpdateOperation, oldPath: string, newPath: string): Promise<void> {
  const storageProvider = editorStore.project.makeScopedStorageProvider();

  if (DEBUG.logUpdates) {
    console.log(`📝 Updating ${operation.filePath} (${operation.updates.length} changes)`);
  }

  try {
    const normalizedPath = operation.filePath.trim().toLowerCase();
    if (normalizedPath.endsWith('.glb')) {
      const fileData = await storageProvider.downloadFile(
        ENGINE.AssetPath.fromString(operation.filePath)
      );
      if (!fileData) {
        throw new Error(`Failed to load file: ${operation.filePath}`);
      }
      const arrayBuffer = fileData instanceof Blob ? await fileData.arrayBuffer() : (fileData as ArrayBuffer);
      const updatedBuffer = rewriteGlbJsonPaths(arrayBuffer, jsonData =>
        ENGINE.WorldSerializer.rewriteAssetPaths(jsonData, oldPath, newPath)
      );
      await storageProvider.uploadFile(
        ENGINE.AssetPath.fromString(operation.filePath),
        new Blob([updatedBuffer], { type: 'model/gltf-binary' })
      );
    } else {
      const fileData = await storageProvider.downloadFileAsJson<unknown>(
        ENGINE.AssetPath.fromString(operation.filePath)
      );
      if (!fileData) {
        throw new Error(`Failed to load file: ${operation.filePath}`);
      }
      const updatedData = ENGINE.WorldSerializer.rewriteAssetPaths(fileData, oldPath, newPath);
      const fileJson = JSON.stringify(updatedData, null, 2);
      await storageProvider.uploadFile(
        ENGINE.AssetPath.fromString(operation.filePath),
        new Blob([fileJson], { type: 'application/json' })
      );
    }

    // Mark asset as dirty to trigger thumbnail regeneration
    const asset = assetsStore.assetTree.files.get(operation.filePath);
    asset?.setDirty();

  } catch (error) {
    console.error(`Failed to update file ${operation.filePath}:`, error);
    throw error;
  }
}

/** Execute the complete update plan */
export async function executeUpdatePlan(plan: PathUpdatePlan): Promise<void> {
  if (DEBUG.logUpdates) {
    console.log(`⚡ Executing update plan: ${plan.affectedAssetCount} files...`);
  }

  // Update all dependent files first (before moving the actual asset)
  for (const operation of plan.updateOperations) {
    await updateFile(operation, plan.oldPath, plan.newPath);
  }

  if (DEBUG.logUpdates) {
    console.log('✅ All files updated successfully.');
  }
}

/** Backup all files that will be modified */
export async function backupAffectedFiles(plan: PathUpdatePlan): Promise<FileBackup[]> {
  const backups: FileBackup[] = [];
  const storageProvider = editorStore.project.makeScopedStorageProvider();

  if (DEBUG.logBackups) {
    console.log(`💾 Backing up ${plan.updateOperations.length + 1} files...`);
  }

  try {
    // Backup the asset being renamed
    const assetContent = await storageProvider.downloadFile(
      ENGINE.AssetPath.fromString(plan.oldPath)
    );
    backups.push({
      path: plan.oldPath,
      content: assetContent,
    });

    // Backup all files that will be updated
    for (const operation of plan.updateOperations) {
      const content = await storageProvider.downloadFile(
        ENGINE.AssetPath.fromString(operation.filePath)
      );
      backups.push({
        path: operation.filePath,
        content,
      });
    }

    if (DEBUG.logBackups) {
      console.log(`💾 Backed up ${backups.length} files.`);
    }
  } catch (error) {
    console.error('Failed to backup files:', error);
    throw error;
  }

  return backups;
}

/** Restore files from backups (for undo) */
export async function restoreFromBackups(backups: FileBackup[]): Promise<void> {
  const storageProvider = editorStore.project.makeScopedStorageProvider();

  if (DEBUG.logBackups) {
    console.log(`♻️ Restoring ${backups.length} files from backup...`);
  }

  try {
    for (const backup of backups) {
      await storageProvider.uploadFile(
        ENGINE.AssetPath.fromString(backup.path),
        backup.content
      );

      // Refresh asset tree entry
      const asset = assetsStore.assetTree.files.get(backup.path);
      asset?.setDirty();
    }

    if (DEBUG.logBackups) {
      console.log(`♻️ Restored ${backups.length} files.`);
    }
  } catch (error) {
    console.error('Failed to restore from backups:', error);
    throw error;
  }
}

